/**
 * The picture a competition shows when nobody has uploaded a poster.
 *
 * Everything is derived from the competition's own name, so the same
 * competition always looks the same and two competitions at one gym never look
 * alike. That is the whole point: tinting these with the gym's colour would put
 * four identical squares in a row on the gym's own page.
 *
 * It fills whatever box it is given and sizes its type in container units, so
 * the same component works as a 200px card thumbnail and as a full-width hero.
 */

/** FNV-1a. Small, stable, and enough to spread names across the hue circle. */
function hashString(value: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return hash
}

/** A deterministic sequence from one seed, so a poster never changes. */
function sequence(seed: number): () => number {
  let state = seed || 1
  return () => {
    state ^= state << 13
    state ^= state >>> 17
    state ^= state << 5
    state >>>= 0
    return state / 0xffffffff
  }
}

interface Hold {
  top: string
  left: string
  size: string
  opacity: number
  round: boolean
}

/** Scattered shapes suggesting holds on a wall, placed from the same seed. */
function holds(next: () => number, count: number): Hold[] {
  return Array.from({ length: count }, () => ({
    top: `${Math.round(next() * 92)}%`,
    left: `${Math.round(next() * 92)}%`,
    size: `${Math.round(6 + next() * 16)}cqw`,
    opacity: 0.08 + next() * 0.16,
    round: next() > 0.35,
  }))
}

interface Props {
  /** Always the seed for the colours and the shapes, drawn or not. */
  name: string
  /** Shown small beneath the name. Left out when the venue is already obvious. */
  gymName?: string | null
  /**
   * Off for thumbnails. Below roughly 100px the name is unreadable texture, and
   * drawing it there only repeats what the row beside it already says.
   */
  showName?: boolean
  className?: string
}

export function CompetitionPoster({ name, gymName, showName = true, className = '' }: Props) {
  const seed = hashString(name)
  const next = sequence(seed)
  const hue = seed % 360
  // A second hue a fifth of the circle away: close enough to agree, far enough
  // to read as two colours rather than one flat wash.
  const hue2 = (hue + 48) % 360

  return (
    <div
      className={`relative flex h-full w-full flex-col justify-end overflow-hidden ${className}`}
      // Container units below need this; without it every poster renders its
      // type at the same size regardless of the box.
      style={{
        containerType: 'inline-size',
        // Comma syntax on purpose: the space-separated CSS Color 4 form is
        // dropped outright by jsdom, so the poster would be invisible in tests.
        backgroundImage: `linear-gradient(145deg, hsl(${hue}, 62%, 32%), hsl(${hue2}, 58%, 18%))`,
      }}
    >
      {holds(next, 9).map((hold, i) => (
        <span
          key={i}
          aria-hidden="true"
          className="absolute"
          style={{
            top: hold.top,
            left: hold.left,
            width: hold.size,
            height: hold.size,
            opacity: hold.opacity,
            backgroundColor: '#fff',
            borderRadius: hold.round ? '9999px' : '22%',
            transform: `rotate(${Math.round(next() * 60 - 30)}deg)`,
          }}
        />
      ))}

      {/* Keeps the name legible whatever the shapes land on. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{ backgroundImage: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent 62%)' }}
      />

      {/* Decorative: the name it draws is already the page's own heading, and
          reading it twice helps nobody. */}
      {showName && (
        <div className="relative p-[7cqw]" aria-hidden="true">
          <p
            className="font-extrabold text-white uppercase"
            style={{ fontSize: '12cqw', lineHeight: 1.02, letterSpacing: '-0.02em' }}
          >
            {name}
          </p>
          {gymName && (
            <p
              className="mt-[2.5cqw] font-semibold text-white/70 uppercase"
              style={{ fontSize: '4.2cqw', letterSpacing: '0.16em' }}
            >
              {gymName}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
